import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Box,
  Center,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconMailCheck } from '@tabler/icons-react';
import { verifyEmailRequest } from './auth-api';
import { ApiError } from '../lib/api-error';

type Status =
  | { kind: 'pending' }
  | { kind: 'verified'; changed: boolean }
  | { kind: 'error'; message: string };

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<Status>({ kind: 'pending' });
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    void (async () => {
      try {
        const res = await verifyEmailRequest(token);
        setStatus({ kind: 'verified', changed: Boolean(res.changed) });
      } catch (err) {
        if (err instanceof ApiError && err.code === 'invalid_or_expired_token') {
          setStatus({
            kind: 'error',
            message:
              'This verification link is invalid or has expired. Sign in and request a new one from your profile.',
          });
        } else if (err instanceof ApiError && err.code === 'email_already_in_use') {
          setStatus({
            kind: 'error',
            message: 'That email address is already linked to another account.',
          });
        } else {
          setStatus({
            kind: 'error',
            message: 'Could not reach the server. Try again in a moment.',
          });
        }
      }
    })();
  }, [token]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Center h="100vh" bg="var(--mantine-color-default-hover)">
      <Paper shadow="md" radius="md" p="xl" w={420} withBorder>
        <Stack gap="md">
          <Box>
            <Title order={2}>Email verification</Title>
            <Text c="dimmed" size="sm">
              Confirming the link you opened from your inbox.
            </Text>
          </Box>

          {status.kind === 'pending' && (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          )}

          {status.kind === 'verified' && (
            <Alert
              icon={status.changed ? <IconMailCheck size={16} /> : <IconCheck size={16} />}
              color="pine"
              variant="light"
            >
              <Text size="sm">
                {status.changed
                  ? 'Your account email has been updated.'
                  : 'Your email address is verified.'}
              </Text>
            </Alert>
          )}

          {status.kind === 'error' && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {status.message}
            </Alert>
          )}

          <Text size="xs" c="dimmed" ta="center">
            <Anchor component={Link} to="/login">
              Continue to sign in
            </Anchor>
          </Text>
        </Stack>
      </Paper>
    </Center>
  );
}
